/* eslint-disable react/no-unknown-property */
import { Suspense, useRef, useMemo, useLayoutEffect, useEffect } from "react";
import { Canvas, useFrame, useLoader, useThree, extend } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

extend({ OrbitControls });

const MODEL_URL = "/models/monowood.glb";

function Model({ rotationSpeed = 0.18, autoRotateRef }) {
  const group = useRef();
  const gltf = useLoader(GLTFLoader, MODEL_URL);

  const scene = useMemo(() => {
    const s = gltf.scene.clone(true);
    // Rhino Z-up → three.js Y-up: rotate -90° around X
    s.rotation.set(-Math.PI / 2, 0, 0);
    s.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(s);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const scale = 2.6 / maxDim;
    s.scale.setScalar(scale);
    s.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
    s.traverse((obj) => {
      if (obj.isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
        if (obj.geometry && !obj.geometry.attributes.normal) {
          obj.geometry.computeVertexNormals();
        }
      }
    });
    return s;
  }, [gltf.scene]);

  useFrame((_, delta) => {
    if (group.current && autoRotateRef.current) {
      group.current.rotation.y += delta * rotationSpeed;
    }
  });

  return (
    <group ref={group}>
      <primitive object={scene} />
    </group>
  );
}

function Controls({ autoRotateRef }) {
  const { camera, gl } = useThree();
  const ref = useRef();

  useEffect(() => {
    const c = new OrbitControls(camera, gl.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.enablePan = false;
    c.minDistance = 2.5;
    c.maxDistance = 12;
    c.minPolarAngle = Math.PI * 0.15;
    c.maxPolarAngle = Math.PI * 0.85;
    c.rotateSpeed = 0.8;
    c.zoomSpeed = 0.7;

    const onStart = () => {
      autoRotateRef.current = false;
    };
    c.addEventListener("start", onStart);
    ref.current = c;

    return () => {
      c.removeEventListener("start", onStart);
      c.dispose();
    };
  }, [camera, gl, autoRotateRef]);

  useFrame(() => ref.current && ref.current.update());
  return null;
}

function Lighting() {
  const dirRef = useRef();
  useLayoutEffect(() => {
    if (dirRef.current) {
      dirRef.current.shadow.mapSize.set(2048, 2048);
      dirRef.current.shadow.camera.near = 0.5;
      dirRef.current.shadow.camera.far = 20;
      dirRef.current.shadow.camera.left = -4;
      dirRef.current.shadow.camera.right = 4;
      dirRef.current.shadow.camera.top = 4;
      dirRef.current.shadow.camera.bottom = -4;
      dirRef.current.shadow.bias = -0.0005;
      dirRef.current.shadow.radius = 2;
    }
  }, []);
  return (
    <>
      {/* Subtle hemisphere — keeps shadows soft but not flat */}
      <hemisphereLight args={["#d8d4c8", "#0a0a0a", 0.35]} />
      {/* Very low ambient — keeps deep shadows */}
      <ambientLight intensity={0.12} />
      {/* Strong key light — high contrast, defines layers */}
      <directionalLight
        ref={dirRef}
        position={[5, 7, 4]}
        intensity={2.2}
        castShadow
      />
      {/* Cool low-intensity fill — softens shadow side without flattening */}
      <directionalLight position={[-5, 2, 3]} intensity={0.25} color="#a8b8c8" />
      {/* Warm rim accent */}
      <directionalLight position={[-3, 1, -5]} intensity={0.45} color="#c88a53" />
    </>
  );
}

function Ground() {
  return (
    <mesh
      receiveShadow
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -1.55, 0]}
    >
      <planeGeometry args={[20, 20]} />
      <shadowMaterial opacity={0.55} />
    </mesh>
  );
}

const ModelViewer = () => {
  const autoRotateRef = useRef(true);

  return (
    <div
      data-testid="model-canvas"
      className="relative aspect-[16/10] md:aspect-[16/9] w-full border border-white/10 bg-gradient-to-b from-[#0a0a0a] to-[#050505] overflow-hidden touch-none"
    >
      {["top-3 left-3", "top-3 right-3", "bottom-3 left-3", "bottom-3 right-3"].map(
        (pos, i) => (
          <span
            key={i}
            className={`absolute ${pos} w-3 h-3 pointer-events-none border-[#c88a53]/50 z-10`}
            style={{
              borderTopWidth: pos.includes("top") ? 1 : 0,
              borderBottomWidth: pos.includes("bottom") ? 1 : 0,
              borderLeftWidth: pos.includes("left") ? 1 : 0,
              borderRightWidth: pos.includes("right") ? 1 : 0,
            }}
          />
        )
      )}

      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{ position: [3.5, 2.4, 4.6], fov: 35 }}
        gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 0.85 }}
      >
        <color attach="background" args={["#070707"]} />
        <fog attach="fog" args={["#050505", 9, 20]} />
        <Lighting />
        <Suspense fallback={null}>
          <Model autoRotateRef={autoRotateRef} />
        </Suspense>
        <Ground />
        <Controls autoRotateRef={autoRotateRef} />
      </Canvas>

      <div className="absolute bottom-3 left-3 text-[0.6rem] tracking-[0.3em] uppercase text-white/40 font-mono-num pointer-events-none z-10">
        MW-01 · Drehen + Zoom mit Maus / Touch
      </div>
    </div>
  );
};

export default ModelViewer;
